import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import T from 'components/T';
import { M } from '@dashboard-experience/mastodon';
import { Report } from 'types/report';
import CandidateStoryForm from './Body/CandidateStoryForm/Container';
import { CandidateStoryFailure, CandidateStorySuccess } from './Body';
import {
  hideChat,
  showChat,
} from '../../../../CandidateLandingPage/chatVisibility';
import { trackAnalyticsEvent } from '../../../../../../actions/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_PROPERTIES,
} from '../../../../../../lib/analytics';
import { ModalOpenContext } from '../../../../../Workflow/ModalOpenContext';

type Props = {
  companyName: string;
  isOpen: boolean;
  toggleCandidateStoryModal: () => void;
  report: Report;
  candidateStorySubmitted: boolean;
  generalInformationOnly: boolean;
  submitError: boolean;
  toggleDisputeModal: () => void;
};

const CandidateStoryModal = ({
  companyName,
  isOpen = false,
  toggleCandidateStoryModal = () => {},
  report,
  candidateStorySubmitted = false,
  generalInformationOnly = false,
  submitError,
  toggleDisputeModal = () => {},
}: Props) => {
  const dispatch = useDispatch<any>();
  const submittedRef = useRef(candidateStorySubmitted);
  submittedRef.current = candidateStorySubmitted;

  useEffect(() => {
    if (isOpen) {
      hideChat();
    } else {
      showChat();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !submittedRef.current) {
      dispatch(trackAnalyticsEvent(ANALYTICS_EVENTS.STORY_STARTED));
    }
  }, [dispatch, isOpen]);

  const closeModal = () => {
    dispatch(
      trackAnalyticsEvent(ANALYTICS_EVENTS.STORY_CLOSED, {
        [ANALYTICS_PROPERTIES.SUBMITTED]:
          candidateStorySubmitted && !submitError,
      }),
    );
    toggleCandidateStoryModal();
  };

  return (
    <M.ComposedModal data-testid='candidate-story-modal' open={isOpen}>
      <M.ModalHeader
        data-testid='candidate-story-modal-title'
        buttonOnClick={closeModal}
      >
        <T
          value='components.Report.CandidateStories.Modal.title'
          company={companyName}
        />
      </M.ModalHeader>
      <M.ModalBody data-testid='candidate-story-modal-body'>
        <ModalOpenContext.Provider value={isOpen}>
          {candidateStorySubmitted && !submitError && (
            <CandidateStorySuccess
              company={companyName}
              formName='CandidateStory'
            />
          )}
          {candidateStorySubmitted && submitError && <CandidateStoryFailure />}
          {!candidateStorySubmitted && (
            <CandidateStoryForm
              companyName={companyName}
              report={report}
              toggleCandidateStoryModal={toggleCandidateStoryModal}
              toggleDisputeModal={toggleDisputeModal}
              generalInformationOnly={generalInformationOnly}
            />
          )}
        </ModalOpenContext.Provider>
      </M.ModalBody>
    </M.ComposedModal>
  );
};

export default CandidateStoryModal;
